import React from "react";
import { CheckCircle, Circle, PenNib, CalendarBlank, ArrowRight, Car, ShieldCheck, CurrencyEur, Clock } from "@phosphor-icons/react";
import { fmtDate, euro } from "@/lib/portalApi";

const AMPEL = {
  rot: { hex: "#D32F2F", soft: "#FEE2E2", idx: 0 },
  gelb: { hex: "#F59E0B", soft: "#FEF3C7", idx: 1 },
  gruen: { hex: "#16A34A", soft: "#DCFCE7", idx: 2 },
};

export default function PortalStatus({ me, onGoto }) {
  if (!me) return null;
  const a = me.ampel || { farbe: "rot", label: "In Bearbeitung" };
  const meta = AMPEL[a.farbe] || AMPEL.rot;
  const anrede = me.mandant?.vorname ? `${me.mandant.vorname} ${me.mandant.nachname || ""}`.trim() : me.mandant?.nachname;

  return (
    <div className="space-y-6" data-testid="portal-status">
      <div>
        <p className="text-[11px] uppercase tracking-[0.18em] text-[#9CA3AF]">Guten Tag{anrede ? `, ${anrede}` : ""}</p>
        <h1 className="font-serif text-4xl sm:text-5xl leading-tight mt-1">Stand Ihrer Angelegenheit</h1>
      </div>

      {/* Ampel */}
      <section className="bg-white rounded-2xl border border-[#E5E7EB] shadow-sm overflow-hidden" data-testid="portal-ampel">
        <div className="flex items-stretch">
          <div className="w-20 sm:w-24 shrink-0 flex flex-col items-center justify-center gap-2.5 py-6 bg-[#0F172A]">
            {["rot", "gelb", "gruen"].map((f) => {
              const on = f === a.farbe;
              return (
                <span
                  key={f}
                  data-testid={`ampel-licht-${f}`}
                  data-active={on ? "true" : "false"}
                  className={`h-9 w-9 sm:h-11 sm:w-11 rounded-full transition-all ${on ? "ampel-pulse" : ""}`}
                  style={{ background: on ? AMPEL[f].hex : "#1F2937", boxShadow: on ? `0 0 22px ${AMPEL[f].hex}99, inset 0 -3px 6px rgba(0,0,0,.25)` : "inset 0 2px 4px rgba(0,0,0,.6)" }}
                />
              );
            })}
          </div>
          <div className="flex-1 p-5 sm:p-7">
            <span className="inline-flex items-center gap-2 text-xs font-semibold rounded-full px-3 py-1" style={{ background: meta.soft, color: meta.hex }}>
              <span className="h-2 w-2 rounded-full" style={{ background: meta.hex }} /> Aktueller Status
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl leading-tight mt-3" data-testid="portal-ampel-label">{a.label}</h2>
            <p className="text-sm text-[#4B5563] mt-2 leading-relaxed" data-testid="portal-ampel-text">{a.beschreibung}</p>
            {me.zahlung?.am && (
              <p className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-[#15803D]" data-testid="portal-zahlung">
                <CurrencyEur size={16} weight="duotone" /> {euro(me.zahlung.betrag)} eingegangen am {fmtDate(me.zahlung.am)}
              </p>
            )}
          </div>
        </div>
      </section>

      {me.unterschrift?.offen > 0 && (
        <button
          onClick={() => onGoto("unterschrift")}
          className="w-full text-left bg-[#1A2238] text-white rounded-2xl p-5 flex items-center gap-4 hover:bg-[#2C3859] transition-colors shadow-md"
          data-testid="portal-cta-unterschrift"
        >
          <span className="h-12 w-12 rounded-full bg-white/10 flex items-center justify-center shrink-0"><PenNib size={24} weight="duotone" /></span>
          <span className="flex-1">
            <span className="block font-medium">Ihre Unterschrift wird benötigt</span>
            <span className="block text-sm text-white/65">{me.unterschrift.offen === 1 ? "1 Dokument" : `${me.unterschrift.offen} Dokumente`} warten auf Ihre digitale Unterschrift.</span>
          </span>
          <ArrowRight size={20} />
        </button>
      )}

      {/* Eckdaten */}
      <section className="grid grid-cols-2 gap-3" data-testid="portal-eckdaten">
        <Fact icon={CalendarBlank} label="Unfall" value={me.unfall?.datum ? fmtDate(me.unfall.datum) : "—"} sub={me.unfall?.ort} />
        <Fact icon={Car} label="Fahrzeug" value={[me.fahrzeug?.marke, me.fahrzeug?.modell].filter(Boolean).join(" ") || "—"} sub={me.fahrzeug?.kennzeichen} mono />
        <Fact icon={ShieldCheck} label="Gegnerische Versicherung" value={me.gegner_versicherung || "—"} />
        <Fact icon={CurrencyEur} label="Geltend gemachter Schaden" value={me.schadensumme ? euro(me.schadensumme) : "wird ermittelt"} testId="portal-schadensumme" />
      </section>

      {/* Schritte */}
      <section className="bg-white rounded-2xl border border-[#E5E7EB] p-5 sm:p-7" data-testid="portal-schritte">
        <p className="text-[11px] uppercase tracking-[0.18em] text-[#9CA3AF] mb-4">So geht es weiter</p>
        <ol className="relative">
          {(me.schritte || []).map((s, i, arr) => (
            <li key={s.key} className="relative flex gap-4 pb-5 last:pb-0" data-testid={`portal-schritt-${s.key}`} data-state={s.erledigt ? "erledigt" : s.aktuell ? "aktuell" : "offen"}>
              {i < arr.length - 1 && <span className={`absolute left-[13px] top-7 bottom-0 w-px ${s.erledigt ? "bg-[#16A34A]/40" : "bg-[#E5E7EB]"}`} />}
              <span className="relative z-10 shrink-0 mt-0.5">
                {s.erledigt ? (
                  <CheckCircle size={28} weight="fill" className="text-[#16A34A]" />
                ) : s.aktuell ? (
                  <span className="h-7 w-7 rounded-full border-2 border-[#D32F2F] bg-white flex items-center justify-center"><span className="h-2.5 w-2.5 rounded-full bg-[#D32F2F] ampel-pulse" /></span>
                ) : (
                  <Circle size={28} className="text-[#D1D5DB]" />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-3">
                  <p className={`font-medium ${s.erledigt ? "text-[#0A192F]" : s.aktuell ? "text-[#D32F2F]" : "text-[#9CA3AF]"}`}>{s.titel}</p>
                  {s.datum && <span className="text-xs text-[#9CA3AF] font-mono shrink-0">{fmtDate(s.datum)}</span>}
                </div>
                {(s.aktuell || s.erledigt) && <p className="text-xs text-[#6B7280] mt-0.5">{s.hinweis}</p>}
              </div>
            </li>
          ))}
        </ol>
      </section>

      {me.termine?.length > 0 && (
        <section className="bg-white rounded-2xl border border-[#E5E7EB] p-5 sm:p-7" data-testid="portal-termine">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#9CA3AF] mb-3">Geplante Schritte Ihrer Kanzlei</p>
          <ul className="divide-y divide-[#F3F4F6]">
            {me.termine.map((t) => (
              <li key={t.id} className="flex items-center gap-3 py-2.5">
                <Clock size={18} weight="duotone" className="text-[#F59E0B] shrink-0" />
                <span className="flex-1 text-sm">{t.titel}</span>
                <span className="text-xs font-mono text-[#6B7280]">{fmtDate(t.faellig_am)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {me.ereignisse?.length > 0 && (
        <section className="bg-white rounded-2xl border border-[#E5E7EB] p-5 sm:p-7" data-testid="portal-ereignisse">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#9CA3AF] mb-3">Letzte Ereignisse</p>
          <ul className="divide-y divide-[#F3F4F6]">
            {me.ereignisse.slice(0, 8).map((e) => (
              <li key={e.id} className="py-2.5 flex items-start gap-3">
                <span className="h-1.5 w-1.5 rounded-full bg-[#1A2238] mt-2 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm">{e.titel}</p>
                  {e.beschreibung && <p className="text-xs text-[#6B7280] truncate">{e.beschreibung}</p>}
                </div>
                <span className="text-xs text-[#9CA3AF] font-mono shrink-0">{fmtDate(e.created_at)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function Fact({ icon: Icon, label, value, sub, mono, testId }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-[#9CA3AF] mb-2"><Icon size={16} weight="duotone" /><span className="text-[10px] uppercase tracking-[0.16em]">{label}</span></div>
      <p className={`font-medium text-[#0A192F] leading-snug ${mono ? "" : ""}`}>{value}</p>
      {sub && <p className="text-xs text-[#6B7280] font-mono mt-0.5">{sub}</p>}
    </div>
  );
}
